import { useEffect } from 'react';
import { useRoutes } from 'react-router-dom';
import mixpanel from 'mixpanel-browser';
import { datadogRum } from '@datadog/browser-rum';
import { ReactNotifications } from 'react-notifications-component';
import 'react-notifications-component/dist/theme.css';
import 'twin.macro';

import Package from '../package.json';

import { _authRoutes, _notFound } from '@auth_module/routes/_authRoutes';
import { _accountRoutes } from '@account_module/routes/_accountRoutes';
import { _insightsRoutes } from '@insights_module/routes/_insightsRoutes';

import { Layout } from '@auth_module/components/Layout';
import { useTheme } from '@app/themes/useTheme';
import { updateCssVariables } from '@app/themes/utils';
import { useAuthContext } from '@auth_module/context/AuthContext';
import { Loader } from '@components/Loader';

import GlobalStyles from '@app/styles/GlobalStyles';
import { useRedirect } from '@auth_module/hooks/useRedirect';
import { useUser } from '@auth_module/hooks/useUser';

mixpanel.init(String(import.meta.env.VITE_MIXPANEL_TOKEN));

window.Beacon('config', {
  messaging: {
    chatEnabled: true,
    contactForm: {
      customFieldsEnabled: true,
      showPrefilledCustomFields: true,
    },
  },
  labels: {
    searchLabel: 'Message',
  },
});

if (import.meta.env.MODE === 'production') {
  datadogRum.init({
    applicationId: import.meta.env.VITE_RUM_APP_ID,
    clientToken: import.meta.env.VITE_RUM_CLIENT_TOKEN,
    site: import.meta.env.VITE_RUM_SITE,
    service: import.meta.env.VITE_RUM_SERVICE,
    env: import.meta.env.MODE,
    version: Package.version,
    defaultPrivacyLevel: 'mask-user-input',
    trackUserInteractions: true,
    sessionSampleRate: 100,
    sessionReplaySampleRate: 100,
    startSessionReplayRecordingManually: true,
  });

  datadogRum.startSessionReplayRecording();
}

function App() {
  const { auth } = useAuthContext();
  const { cssVars, moduleTheme } = useTheme();
  const { navigateToDashboard, isLoading: isFetchingDataBeforeRedirect } = useRedirect();

  // We define the routes for each module as a RouteObject[] inside the module routes folder.
  // Routes from each module must be passed to the useRoutes hook in order to be exposed and render content.
  const appRoutes = useRoutes([
    ..._authRoutes(),
    ..._accountRoutes(),
    ..._insightsRoutes(),
    ..._notFound(),
  ]);

  const { user, organization, isFetching, error } = useUser();

  useEffect(() => {
    if (user && organization) {
      // This function creates visitors and accounts in Pendo
      // Please use Strings, Numbers, or Bools for value types.
      window.pendo.initialize({
        visitor: {
          id: user.email.toLowerCase(), // recommended from trition to use email as id
          email: user.email,
          first_name: user.first_name,
          last_name: user.last_name,
          full_name: `${user.first_name} ${user.last_name}`, // recommended from pendo docs
          user_id: user.id,
          sounder_account_id: user.account_id,
          organization_id: user.organization_id,
        },
        account: {
          id: organization.id,
          name: organization.name,
        },
      });
    }
  }, [user, organization]);

  useEffect(() => cssVars && updateCssVariables(cssVars), [cssVars]);

  useEffect(() => {
    if (import.meta.env.MODE === 'production' && auth.user.id) {
      datadogRum.setUser({
        id: String(auth.user.id),
        email: auth.user.email,
      });
    }
  }, [auth.user.id]);

  useEffect(() => {
    navigateToDashboard();
  }, [navigateToDashboard]);

  if (isFetching || isFetchingDataBeforeRedirect)
    return (
      <Layout.Background>
        <Loader size='lg' theme='dark' />
      </Layout.Background>
    );

  if (error) {
    return (
      <Layout.Background>
        <div tw='text-center text-text-and-icon-invert'>
          <p>Sorry, something went wrong. Please refresh page or try again later.</p>
          <p>
            Error: <code>{error?.message}</code>
          </p>
        </div>
      </Layout.Background>
    );
  }

  return (
    <>
      <GlobalStyles module={moduleTheme} />
      <ReactNotifications />
      {appRoutes}
    </>
  );
}

export default App;
